<template>
  <v-app id="inspire" :class="{'logged-out-app': !authenticated}">
    <v-app-bar app flat :color="authenticated ? 'var(--color-viewport-bg)' : 'var(--color-primary-container)'" style="padding-right: 20px;">
      <v-btn icon @click="drawer = !drawer" v-if="(authenticated || $vuetify.breakpoint.mobile) && $vuetify.breakpoint.mdAndDown" class="mr-1">
        <v-icon>{{ drawer ? 'mdi-menu-open' : 'mdi-menu' }}</v-icon>
      </v-btn>
      <router-link to="/"><img src="@/assets/images/IFPI_Logo_blue_Primary.svg" alt="IFPI Sverige" style="margin-top: 8px; height: 34px;" /></router-link>
      <v-spacer/>

      <v-row style="max-width: 20em; margin-right: 10px; margin-top: 12px;" v-if="authenticated && !$vuetify.breakpoint.mobile">
        <v-select
          filled
          rounded
          dense
          :placeholder="$t('rightsholders.placeholder')"
          :items="rightholdersList"
          @change="onChangeRightsholder"
          :value="$store.getters['app/currentRightsholder'].id"
          class="t-rh-select"
        />
      </v-row>

      <template v-if="!authenticated && !$vuetify.breakpoint.mobile">
        <v-btn text plain :to="{name: 'AppLogin'}" class="mr-2">{{ $t('app.login') }}</v-btn>
        <v-btn text plain :to="{name: 'AppDocuments'}" class="mr-2">{{ $t('app.rightsholder-forms') }}</v-btn>
      </template>

      <v-btn icon @click="notificationsViewed = true" class="t-notifications-btn" v-if="authenticated && currentRightsholderId">
        <v-badge
            color="primary"
            :value="$store.getters['app/unreadNotificationsCount'] > 0"
            :content="String($store.getters['app/unreadNotificationsCount'])"
        >
          <v-icon>mdi-bell-outline</v-icon>
        </v-badge>
      </v-btn>

      <v-menu bottom left>
        <template v-slot:activator="{on, attrs}">
          <v-btn icon v-on="on" v-bind="attrs" class="t-lang-switcher">
            <flag :iso="currentFlag"/>
          </v-btn>
        </template>

        <v-list>
          <v-list-item v-for="item in languages" :key="item.value" @click="switchLocale(item.lang)">
            <v-list-item-title>{{ item.text }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </v-app-bar>

    <CookieConsentDialog v-model="consentDialogOpen" />

    <v-dialog
        :value="forcePickRHDisplayed"
        max-width="30%"
        persistent
    >
      <v-card>
        <v-card-title>{{$t('common.forcePicker.title') }}</v-card-title>
        <v-card-subtitle>{{$t('common.forcePicker.subtitle') }}</v-card-subtitle>

        <v-card-text>
          <v-select
            :placeholder="$t('common.forcePicker.placeholder')"
            :items="rightholdersList"
            :value="$store.getters['app/currentRightsholder']"
            @change="onChangeRightsholder"
            class="t-select-rightsholder"
          />
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-navigation-drawer
        v-model="notificationsViewed"
        fixed
        right
        temporary
        :width="$vuetify.breakpoint.mobile ? '90%' : '400px'"
    >
      <NotificationsPanel></NotificationsPanel>
    </v-navigation-drawer>

    <v-main>
      <div class="app-body">
        <v-navigation-drawer
            v-if="authenticated || $vuetify.breakpoint.mobile"
            :absolute="$vuetify.breakpoint.lgAndUp"
            :fixed="!$vuetify.breakpoint.lgAndUp"
            :permanent="$vuetify.breakpoint.lgAndUp"
            :temporary="!$vuetify.breakpoint.lgAndUp"
            :mini-variant="!drawer && $vuetify.breakpoint.lgAndUp"
            v-model="drawer"
            :class="{mini: !drawer}"
            :style="$vuetify.breakpoint.lgAndUp ? 'top: var(--space-24)' : 'top: 0'"
        >
          <v-layout start column fill-height class="mt-n2">
            <v-btn icon @click.stop="drawer = !drawer" class="ma-2 align-self-start" v-if="$vuetify.breakpoint.lgAndUp">
              <v-icon>{{ drawer ? 'mdi-menu-open' : 'mdi-menu' }}</v-icon>
            </v-btn>
            <UserProfileItem :mini="!drawer && !$vuetify.breakpoint.mobile"/>
            <v-layout justify-space-between column fill-height>
              <MainMenu @open-consent-dialog="consentDialogOpen = true"/>
              <v-container class="ma-0 pa-0">
                <LangaugeSwitcher v-show="false"/>
                <AppVersion v-show="false"/>
              </v-container>
            </v-layout>
          </v-layout>
        </v-navigation-drawer>

        <div class="app-content" :class="{'drawer-open': drawer && authenticated, 'drawer-mini': !drawer && !$vuetify.breakpoint.mobile && authenticated, 'logged-out': !authenticated}">
          <h1 v-if="pageTitle && !(['AppLogin', 'AppStart'].includes($route.name))" class="mb-0 pb-0 app-page-title display-1">{{ pageTitle }}</h1>
          <slot></slot>
          <GeneralNotificationForms></GeneralNotificationForms>
          <div v-if="!authenticated" class="logged-out-footer">
            <div class="logged-out-footer__cols">
              <div class="logged-out-footer__col">
                <p class="logged-out-footer__col-title">{{ $t('app.footer.col1-title') }}<br/>{{ $t('app.footer.col1-contact') }}</p>
                <p class="logged-out-footer__col-text">
                  {{ $t('app.footer.col1-phone') }}<br/>
                  <a class="logged-out-footer__col-link" :href="`mailto:${$t('app.footer.col1-email')}`">{{ $t('app.footer.col1-email') }}</a>
                </p>
              </div>
              <div class="logged-out-footer__col logged-out-footer__col--center">
                <div class="logged-out-footer__social">
                  <a href="https://www.facebook.com/IfpiSverige" target="_blank" rel="noopener noreferrer">
                    <img src="@/assets/icons/facebook.svg" alt="Facebook" class="logged-out-footer__social-icon"/>
                  </a>
                  <a href="https://www.instagram.com/ifpisverige/" target="_blank" rel="noopener noreferrer">
                    <img src="@/assets/icons/instagram.svg" alt="Instagram" class="logged-out-footer__social-icon"/>
                  </a>
                  <a href="https://www.linkedin.com/company/ifpi-sweden/" target="_blank" rel="noopener noreferrer">
                    <img src="@/assets/icons/linkedin.svg" alt="LinkedIn" class="logged-out-footer__social-icon"/>
                  </a>
                </div>
              </div>
              <div class="logged-out-footer__col logged-out-footer__col--end">
                <p class="logged-out-footer__col-text">
                  <span class="logged-out-footer__col-text--bold">{{ $t('app.footer.col3-name') }}</span><br/>
                  {{ $t('app.footer.col3-care') }}<br/>
                  {{ $t('app.footer.col3-street') }}<br/>
                  {{ $t('app.footer.col3-postal') }}
                </p>
              </div>
            </div>
            <a :href="$t('app.footer.privacy-policy-url')" target="_blank" class="logged-out-footer__privacy">{{ $t('app.footer.privacy-policy') }}</a>
            <span class="logged-out-footer__copy">{{ $t('app.footer.copyright') }}</span>
          </div>
        </div>
      </div>
    </v-main>
  </v-app>
</template>

<script>
import MainMenu from "@/components/common/MainMenu";
import LangaugeSwitcher from "@/components/LangaugeSwitcher";
import AppVersion from "@/components/AppVersion";
import UserProfileItem from "@/components/UserProfileItem";
import NotificationsPanel from '@/components/common/notifications/NotificationsPanel';
import {mapActions, mapGetters} from "vuex";
import GeneralNotificationForms from '@/components/common/notifications/GeneralNotificationForms';
import CookieConsentDialog, { hasConsentChoice, getConsent, loadConsentScript } from '@/components/common/CookieConsentDialog';

export default {
  data: () => ({
    drawer: true,
    notificationsViewed: false,
    alertDialog: false,
    consentDialogOpen: false,
  }),

  components: {
    MainMenu,
    LangaugeSwitcher,
    AppVersion,
    UserProfileItem,
    NotificationsPanel,
    GeneralNotificationForms,
    CookieConsentDialog,
  },

  methods: {
    ...mapActions('app', ['switchLocale']),

    onChangeRightsholder(itemId) {
      this.$store.dispatch('app/resetCurrentRightsholder', itemId);
    }
  },

  computed: {
    ...mapGetters('app', ['currentRightsholder']),

    forcePickRHDisplayed() {
      return this.$store.getters['app/currentRightsholder'].id === null && this.rightholdersList.length > 0
    },
    rightholdersList() {
      if (!this.$store.getters["app/rightsholders"].list) {
        return [];
      }
      return this.$store.getters["app/rightsholders"].list.map(item => ({value: item.id, text: item.name}));
    },

    authenticated() {
      return this.$store.getters["app/isAuthenticated"];
    },

    currentFlag() {
      return this.languages.find(({lang}) => lang === this.language).flag;
    },

    language() {
      return this.$store.getters["app/locale"];
    },
    languages() {
      return [
        {text: this.$t('common.languages.en'), flag: "GB", lang: 'en'},
        {text: this.$t('common.languages.sv'), flag: "SE", lang: 'sv'},
      ];
    },

    pageTitle() {
      return this.$t(this.$route.meta.title) || this.$t('app.name');
    },

    currentRightsholderId() {
      return this.currentRightsholder.id;
    }

  },

  watch: {
    authenticated: {
      immediate: true,
      handler(val) {
        document.body.classList.toggle('logged-out', !val);
      }
    }
  },

  mounted() {
    if (!hasConsentChoice()) {
      this.consentDialogOpen = true;
    } else if (getConsent() === 'accepted') {
      loadConsentScript();
    }

    // setTimeout(async () => {
    //   const rhs = this.$store.getters["app/rightsholders"].list;
    //   for (let rh of rhs) {
    //
    //     // eslint-disable-next-line no-await-in-loop
    //     const result = await API.actions.Staging.getList(rh.id);
    //
    //     if (result.data.count >= 0) {
    //       console.log(rh.id + ' \t ' + rh.name  + ' \t ' + result.data.count);
    //     }
    //   }
    // }, 3000);

  }
};
</script>

<style lang="scss">
.v-main__wrap {
  overflow: visible;
}

.logged-out-app,
.logged-out-app .v-main,
.logged-out-app .v-main__wrap {
  background-color: var(--color-primary-container) !important;
}

.logged-out-app .v-app-bar .v-toolbar__content {
  padding-left: var(--layout-margin-mobile) !important;
  padding-right: var(--layout-margin-mobile) !important;
}

@media (min-width: 1264px) {
  .logged-out-app .v-app-bar .v-toolbar__content {
    padding-left: var(--layout-margin-desktop) !important;
    padding-right: var(--layout-margin-desktop) !important;
  }

  .logged-out-app .app-content .v-card__text,
  .logged-out-app .app-content .v-card__actions,
  .logged-out-app .app-content > .container {
    padding-left: var(--layout-margin-desktop) !important;
    padding-right: var(--layout-margin-desktop) !important;
  }
}

@media (max-width: 1263px) {
  .logged-out-app .app-content .v-card__text,
  .logged-out-app .app-content .v-card__actions,
  .logged-out-app .app-content > .container {
    padding-left: var(--layout-margin-mobile) !important;
    padding-right: var(--layout-margin-mobile) !important;
  }
}

.logged-out-footer {
  width: 100%;
  background-color: var(--color-on-secondary-fixed);
  display: flex;
  flex-direction: column;
  padding: 40px 0 32px;

  @media (min-width: 1264px) {
    padding-top: 80px;
  }

  @media (min-width: 1264px) {
    height: 316px;
  }

  &__cols {
    display: flex;
    height: 80px;

    @media (max-width: 1263px) {
      flex-direction: column;
      height: auto;
      gap: 24px;
      padding: 0 var(--layout-margin-mobile);
    }
    max-width: 1200px;
    width: 100%;
    margin: 0 auto;
    padding: 0 120px;
  }

  &__col {
    flex: 1;

    &--center {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    &--end {
      display: flex;
      justify-content: flex-end;

      @media (max-width: 1263px) {
        justify-content: center;
      }
    }

    @media (max-width: 1263px) {
      text-align: center;
    }
  }

  &__social {
    width: 180px;
    height: 24px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  &__social-icon {
    width: 24px;
    height: 24px;
  }

  &__col-title,
  &__col-text {
    color: var(--color-primary-container);
    font-family: var(--font-family);
    font-size: var(--font-size-14);
    font-weight: var(--font-weight-medium);
    line-height: var(--line-height-20);
    letter-spacing: var(--letter-spacing-10);
    margin-bottom: 0;
    display: block;
  }

  &__col-title {
    margin-bottom: 8px;
  }

  &__col-text--bold {
    font-weight: var(--font-weight-semibold);
  }

  &__col-link {
    color: var(--color-primary-container) !important;
    text-decoration: underline;
    opacity: 0.85;
  }

  &__privacy {
    display: block;
    margin-top: 40px;
    margin-bottom: 24px;
    text-align: center;
    color: var(--color-primary-container) !important;
    font-family: 'DM Sans', sans-serif;
    font-weight: 500;
    font-size: 14px;
    line-height: 20px;
    letter-spacing: 0.1px;
    text-decoration: underline solid !important;
    text-decoration-skip-ink: auto;
  }

  &__copy {
    margin-top: 0;
    text-align: center;
    color: var(--color-primary-container);
    font-family: var(--font-family);
    font-size: var(--font-size-12);
    font-weight: var(--font-weight-regular);
    line-height: var(--line-height-16);
    letter-spacing: var(--letter-spacing-40);
  }
}

.app-body {
  position: relative;
  min-height: 100%;
  display: flex;
}

.app-page-title {
  font-family: var(--font-family);
  font-size: var(--font-size-24);
  font-weight: var(--font-weight-extrabold);
  line-height: var(--line-height-32);
  letter-spacing: var(--letter-spacing-0);
  padding: 0 0 var(--space-24) 0;

}

.app-content.logged-out .app-page-title {
  margin-top: var(--space-24);
}

@media (min-width: 1264px) {
  .app-content.logged-out .app-page-title {
    padding-left: var(--layout-margin-desktop);
  }
}

@media (max-width: 1263px) {
  .app-content.logged-out .app-page-title {
    padding-left: var(--layout-margin-mobile);
    margin-left: 0 !important;
  }
}

.app-content {
  flex: 1;
  min-width: 0;
  padding: var(--space-24) var(--space-16);

  @media (max-width: 600px) {
    padding-left: 0;
    padding-right: 0;

    .app-page-title {
      margin-left: var(--space-16) !important;
    }
  }
  background-color: var(--color-viewport-bg);
  transition: padding-left 0.2s cubic-bezier(0.4, 0, 0.2, 1);

  &.drawer-open {
    @media (min-width: 1264px) {
      padding-left: 256px;
    }
  }

  &.drawer-mini {
    padding-left: 56px;
  }

  &.logged-out {
    display: flex;
    flex-direction: column;
    padding-top: 0;
    padding-left: 0;
    padding-right: 0;
    padding-bottom: 0;
    margin-bottom: 0;
    background-color: var(--color-primary-container) !important;

    .logged-out-footer {
      margin-top: auto;
    }
  }
}

@media (min-width: 960px) {
  .v-navigation-drawer .v-navigation-drawer__content {
    padding-right: var(--space-24);
  }
}

.v-navigation-drawer .v-list-item {
  height: 56px;
  border-radius: 28px;
  padding: 0 16px;
  overflow: hidden;

  .v-list-item__title {
    font-family: var(--font-family) !important;
    font-size: var(--font-size-14) !important;
    font-weight: var(--font-weight-medium) !important;
    line-height: var(--line-height-20) !important;
    letter-spacing: var(--letter-spacing-10) !important;
  }

  &.v-list-item--active::before {
    opacity: 0;
  }
}

.v-navigation-drawer .v-list-item--active:not(.v-list-group__header) {
  background-color: var(--color-nav-active-bg);
}

.v-navigation-drawer .v-list-group__items .v-list-item {
  padding-left: 40px;
}

.v-navigation-drawer .v-navigation-drawer__content {
  scrollbar-width: none; /* Firefox */
  &::-webkit-scrollbar { display: none; } /* Chrome/Safari/Edge */
}

@media (max-width: 1263px) {
  .v-navigation-drawer .v-navigation-drawer__content {
    padding: 24px 16px;
  }
}

.v-navigation-drawer__border {
  background-color: transparent !important;
  color: transparent !important;
}

.v-navigation-drawer.mini {
  .v-toolbar__content {
    justify-content: center;
    padding: 0;

    .v-btn.v-btn--icon.v-app-bar__nav-icon {
      margin-right: 0;
    }
  }

  .v-navigation-drawer__content {
    padding-right: 0 !important;
  }

  .v-list-item {
    justify-content: center;
    align-items: center;
    padding: 0 !important;

    .v-list-item__icon,
    .v-list-item__action {
      margin: 0 0 0 0px !important;
      align-self: center;
    }
  }
}
</style>
